/**
 * Reduced-motion preference for the Scare scene.
 *
 * Same storage-agnostic seam `sigmashake-pop/client/prefs.ts` uses for its own
 * reduced-motion pref (see `loadReducedMotionPref`/`saveReducedMotionPref`
 * there): every function here takes a small `PrefsStorage` rather than
 * reaching for `window.localStorage` itself, so precedence and persistence
 * are unit-testable with a plain in-memory fake and no DOM. A storage
 * failure (quota, private browsing, disabled storage) degrades to "no
 * explicit choice" rather than throwing — this is a presentation
 * preference, not something that may ever block the warning screen or the
 * scare itself.
 */

export interface PrefsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const REDUCED_MOTION_KEY = "scarymaze.reducedMotion";

function safeGetItem(storage: PrefsStorage | undefined, key: string): string | null {
  if (storage === undefined) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(storage: PrefsStorage | undefined, key: string, value: string): void {
  if (storage === undefined) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Quota exceeded, private-mode storage rejection, or a disabled storage
    // API: the toggle just doesn't persist across sessions this time.
  }
}

/**
 * `null` means "no explicit in-game choice yet" — distinct from a stored
 * `false`, which is a real "I do want motion" choice. An absent key or an
 * unrecognized stored value both read as `null`.
 */
export function readReducedMotionOverride(storage: PrefsStorage | undefined): boolean | null {
  const raw = safeGetItem(storage, REDUCED_MOTION_KEY);
  if (raw === "true") return true;
  if (raw === "false") return false;
  return null;
}

export function writeReducedMotionOverride(
  storage: PrefsStorage | undefined,
  value: boolean,
): void {
  safeSetItem(storage, REDUCED_MOTION_KEY, value ? "true" : "false");
}

/**
 * An explicit choice (the warning screen's checkbox, persisted via
 * `readReducedMotionOverride`) always wins. Absent one, defer to the OS
 * `prefers-reduced-motion` signal via an injected `matchMedia` (so this stays
 * pure and DOM-free for tests). No explicit choice and no `matchMedia`
 * available (non-browser environment) fails closed to full motion, matching
 * every browser's own default when the media feature can't be evaluated.
 */
export function resolveReducedMotion(
  explicit: boolean | null,
  matchMedia: ((query: string) => { matches: boolean }) | undefined,
): boolean {
  if (explicit !== null) return explicit;
  if (matchMedia === undefined) return false;
  try {
    return matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}
